import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Search, Package } from "lucide-react";
import { useAllStorageBins, useStorageLocations, type StorageBinRow } from "@/hooks/useStorageBins";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPartNames: string[]; // for the pill
  onAssign: (binId: string) => Promise<void> | void;
  /** Optional dialog title; defaults to "Assign to bin". */
  title?: string;
}

type FilterKey = "location" | "aisle" | "bay" | "level" | "shelf";

export function BinAssignModal({ open, onOpenChange, selectedPartNames, onAssign, title }: Props) {
  const { t } = useTranslation("harvest");
  const { data: bins = [] } = useAllStorageBins();
  const { data: locations = [] } = useStorageLocations();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    location: "", aisle: "", bay: "", level: "", shelf: "",
  });

  useEffect(() => {
    if (!open) {
      setQuery("");
      setFilters({ location: "", aisle: "", bay: "", level: "", shelf: "" });
    }
  }, [open]);

  const locName = (id: string) => locations.find((l) => l.id === id)?.name ?? "—";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bins.filter((b) => {
      if (q && !b.address.toLowerCase().includes(q)) return false;
      if (filters.location && b.location_id !== filters.location) return false;
      if (filters.aisle && String(b.aisle) !== filters.aisle) return false;
      if (filters.bay && String(b.bay) !== filters.bay) return false;
      if (filters.level && String(b.level) !== filters.level) return false;
      if (filters.shelf && String(b.shelf) !== filters.shelf) return false;
      return true;
    });
  }, [bins, query, filters]);

  // Context-aware option lists (values present in the currently-filtered set, ignoring the filter being shown)
  const optionsFor = (key: FilterKey): string[] => {
    const partial = bins.filter((b) => {
      const q = query.trim().toLowerCase();
      if (q && !b.address.toLowerCase().includes(q)) return false;
      if (key !== "location" && filters.location && b.location_id !== filters.location) return false;
      if (key !== "aisle" && filters.aisle && String(b.aisle) !== filters.aisle) return false;
      if (key !== "bay" && filters.bay && String(b.bay) !== filters.bay) return false;
      if (key !== "level" && filters.level && String(b.level) !== filters.level) return false;
      if (key !== "shelf" && filters.shelf && String(b.shelf) !== filters.shelf) return false;
      return true;
    });
    const vals = new Set<string>();
    partial.forEach((b) => {
      if (key === "location") vals.add(b.location_id);
      else vals.add(String(b[key as keyof StorageBinRow] as number));
    });
    const arr = Array.from(vals);
    if (key === "location") return arr;
    return arr.sort((a, b) => Number(a) - Number(b));
  };

  const pillLabel =
    selectedPartNames.length === 0
      ? ""
      : selectedPartNames.length === 1
        ? selectedPartNames[0]
        : t("process.partsPill", { count: selectedPartNames.length, defaultValue: "{{count}} parts" });

  const handlePick = async (binId: string) => {
    await onAssign(binId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden top-[10vh] !translate-y-0 max-h-[80vh] flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-base">
            {title ?? t("process.assignModalTitle", { defaultValue: "Assign to bin" })}
          </DialogTitle>
          {pillLabel && (
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-muted/70 border border-border/60 text-foreground text-xs font-medium">
                {pillLabel}
              </span>
            </div>
          )}
        </DialogHeader>

        <div className="px-5 pb-3 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("process.searchBin", { defaultValue: "Search bin code…" })}
              className="pl-9"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <FilterSelect
              label={t("process.filters.location", { defaultValue: "Location" })}
              value={filters.location}
              onChange={(v) => setFilters((f) => ({ ...f, location: v }))}
              options={optionsFor("location").map((id) => ({ value: id, label: locName(id) }))}
            />
            {(["aisle", "bay", "level", "shelf"] as FilterKey[]).map((k) => (
              <FilterSelect
                key={k}
                label={t(`process.filters.${k}`, { defaultValue: k.charAt(0).toUpperCase() + k.slice(1) })}
                value={filters[k]}
                onChange={(v) => setFilters((f) => ({ ...f, [k]: v }))}
                options={optionsFor(k).map((v) => ({ value: v, label: v }))}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border-t border-border py-2 px-2">
          {filtered.length === 0 ? (
            <div className="px-5 py-8 text-sm text-muted-foreground text-center">
              {t("process.noBinsFound", { defaultValue: "No bins match these filters." })}
            </div>
          ) : (
            <ul>
              {filtered.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => handlePick(b.id)}
                    className="w-full flex items-center justify-between rounded-md px-3 py-2 my-0.5 hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 text-primary font-mono text-sm">
                        <Package className="w-3.5 h-3.5" />
                        {b.address}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">{locName(b.location_id)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const ALL = "__all__";
  return (
    <SearchableSelect
      className="h-8 w-auto gap-1 text-xs"
      size="sm"
      value={value || ALL}
      onChange={(v) => onChange(!v || v === ALL ? "" : v)}
      placeholder={label}
      searchPlaceholder={`Search ${label.toLowerCase()}…`}
      emptyMessage="No matches"
      items={[{ value: ALL, label: "All" }, ...options]}
    />
  );
}

